import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/UI/Button';
import { AgentBotActuationSection } from '../../components/Atendimento/AgentBotActuationSection';
import { AgentBotClassificationEditor } from '../../components/Atendimento/AgentBotClassificationEditor';
import { AgentBotSignatureSection } from '../../components/Atendimento/AgentBotSignatureSection';
import { agentBotService } from '../../services/agentBotService';
import { inboxService } from '../../services/inboxService';
import { supportGroupService } from '../../services/supportGroupService';
import { DocumentScopeMode, workspaceService } from '../../services/workspaceService';
import { AgentBot } from '../../types/agentBot';
import { Inbox } from '../../types/inbox';
import { SupportGroup } from '../../types/supportGroup';
import { AgentBotType, AGENT_BOT_TYPE_LABELS } from '../../types/atendimentoEnums';
import { useToast } from '../../components/UI/ToastProvider';
import { useAuth } from '../../contexts/AuthContext';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import { helpdeskRoutes } from '../../routes/helpdeskRoutes';

type AgentBotEditTab = 'geral' | 'atuacao' | 'classificacao' | 'assinatura';

interface WorkspaceOption {
  id: string;
  name: string;
  documentScopeMode: DocumentScopeMode;
  usableInExternalBot: boolean;
}

const SCOPE_MODE_LABELS: Record<DocumentScopeMode, string> = {
  bypass: 'Sem restrição documental (bypass)',
  allowlist: 'Lista de permissão (allowlist)',
  blocklist: 'Lista de bloqueio (blocklist)',
};

const getExternalBotRestrictionReason = (
  mode: DocumentScopeMode,
  usableInExternalBot: boolean,
): string | null => {
  if (usableInExternalBot)
  {
    return null;
  }

  if (mode === 'bypass')
  {
    return 'Em bypass o agente acessa o repositório completo do tenant. Por segurança, bots de atendimento externo não respondem neste modo — configure allowlist ou blocklist com documentos.';
  }

  if (mode === 'allowlist')
  {
    return 'Allowlist sem documentos ou pastas vinculados. Enquanto a lista estiver vazia, o escopo equivale ao repositório completo e o bot externo não responde.';
  }

  return 'Blocklist sem exclusões configuradas. Lista vazia equivale ao repositório completo; bots de atendimento externo não respondem até haver pelo menos uma exclusão.';
};

const parseTab = (value: string | null): AgentBotEditTab => {
  if (value === 'atuacao' || value === 'classificacao' || value === 'assinatura')
  {
    return value;
  }

  return 'geral';
};

const normalizeScopeMode = (value?: string): DocumentScopeMode => {
  if (value === 'allowlist' || value === 'blocklist')
  {
    return value;
  }

  return 'bypass';
};

export const AgentBotFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.isAdmin === true;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bot, setBot] = useState<AgentBot | null>(null);
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [groups, setGroups] = useState<SupportGroup[]>([]);
  const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);
  const [activeTab, setActiveTab] = useState<AgentBotEditTab>(parseTab(searchParams.get('tab')));
  const [form, setForm] = useState({
    name: '',
    description: '',
    botType: AgentBotType.KnowCopilot,
    workspaceId: '',
    outgoingUrl: '',
  });

  useEffect(() => {
    const requested = parseTab(searchParams.get('tab'));
    if (!isEdit && requested !== 'geral')
    {
      setActiveTab('geral');
      return;
    }

    setActiveTab(requested);
  }, [isEdit, searchParams]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try
      {
        const [inboxesData, groupsData, workspacesData] = await Promise.all([
          inboxService.getAll(),
          supportGroupService.getAll(),
          workspaceService.getWorkspaces(),
        ]);
        setInboxes(inboxesData);
        setGroups(groupsData);
        setWorkspaces(workspacesData.map(workspace => ({
          id: workspace.id,
          name: workspace.name,
          documentScopeMode: normalizeScopeMode(workspace.documentScopeMode),
          usableInExternalBot: workspace.usableInExternalBot === true,
        })));

        if (isEdit && id)
        {
          const loaded = await agentBotService.getById(id);
          setBot(loaded);
          setForm({
            name: loaded.name,
            description: loaded.description ?? '',
            botType: loaded.botType,
            workspaceId: loaded.workspaceId ?? '',
            outgoingUrl: loaded.outgoingUrl ?? '',
          });
        }
      }
      catch (error)
      {
        addToast(apiErrorMessage(error, 'Erro ao carregar robô'), 'error');
        if (isEdit)
        {
          navigate(helpdeskRoutes.settingsBots, { replace: true });
        }
      }
      finally
      {
        setLoading(false);
      }
    };

    void load();
  }, [id, isEdit]);

  const selectedWorkspace = useMemo(
    () => workspaces.find(workspace => workspace.id === form.workspaceId) ?? null,
    [workspaces, form.workspaceId],
  );

  const requiresCompatibleWorkspace = form.botType === AgentBotType.KnowCopilot;
  const selectedWorkspaceInvalid = requiresCompatibleWorkspace
    && Boolean(form.workspaceId)
    && selectedWorkspace !== null
    && !selectedWorkspace.usableInExternalBot;
  const canSave = !saving
    && (!requiresCompatibleWorkspace || !selectedWorkspaceInvalid);

  const selectTab = (tab: AgentBotEditTab) => {
    if (!isEdit && tab !== 'geral')
    {
      addToast('Salve o robô para configurar atuação, classificação e assinatura.', 'info');
      return;
    }

    setActiveTab(tab);
    if (tab === 'geral')
    {
      setSearchParams({}, { replace: true });
      return;
    }

    setSearchParams({ tab }, { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim())
    {
      addToast('Nome é obrigatório', 'error');
      return;
    }

    if (selectedWorkspaceInvalid)
    {
      addToast(
        'Selecione um agente compatível com atendimento externo (allowlist ou blocklist com documentos).',
        'error',
      );
      return;
    }

    setSaving(true);
    try
    {
      const payload = {
        name: form.name.trim(),
        description: form.description || undefined,
        botType: form.botType,
        workspaceId: form.workspaceId || undefined,
        outgoingUrl: form.outgoingUrl || undefined,
      };

      if (isEdit && id)
      {
        const updated = await agentBotService.update(id, payload);
        setBot(updated);
        addToast('Robô atualizado', 'success');
      }
      else
      {
        const created = await agentBotService.create(payload);
        addToast('Robô criado', 'success');
        navigate(helpdeskRoutes.settingsBotEdit(created.id), { replace: true });
      }
    }
    catch (error)
    {
      addToast(apiErrorMessage(error, 'Erro ao salvar robô'), 'error');
    }
    finally
    {
      setSaving(false);
    }
  };

  if (loading)
  {
    return (
      <div className="flex justify-center py-12">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
      </div>
    );
  }

  const tabClass = (tab: AgentBotEditTab, disabled: boolean) => {
    const active = activeTab === tab;
    if (disabled)
    {
      return 'px-6 py-3 font-medium text-gray-400 cursor-not-allowed';
    }

    return `px-6 py-3 font-medium ${
      active
        ? 'border-b-2 border-blue-500 text-blue-600'
        : 'text-gray-500 hover:text-gray-700'
    }`;
  };

  return (
    <div className="max-w-4xl">
      <Link to={helpdeskRoutes.settingsBots} className="text-sm text-blue-600 hover:underline mb-4 inline-block">
        ← Voltar para robôs
      </Link>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {isEdit ? 'Editar robô' : 'Novo robô'}
      </h1>

      <div className="border-b mb-6">
        <nav className="flex gap-1" aria-label="Abas do robô">
          <button
            type="button"
            className={tabClass('geral', false)}
            onClick={() => selectTab('geral')}
          >
            Geral
          </button>
          <button
            type="button"
            className={tabClass('atuacao', !isEdit)}
            onClick={() => selectTab('atuacao')}
            title={!isEdit ? 'Salve o robô para configurar atuação' : undefined}
          >
            Atuação
          </button>
          <button
            type="button"
            className={tabClass('classificacao', !isEdit)}
            onClick={() => selectTab('classificacao')}
            title={!isEdit ? 'Salve o robô para configurar classificação' : undefined}
          >
            Classificação
          </button>
          <button
            type="button"
            className={tabClass('assinatura', !isEdit)}
            onClick={() => selectTab('assinatura')}
            title={!isEdit ? 'Salve o robô para configurar a assinatura' : undefined}
          >
            Assinatura
          </button>
        </nav>
      </div>

      {activeTab === 'geral' && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full border rounded-md px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border rounded-md px-3 py-2 text-sm"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select
              value={form.botType}
              onChange={e => setForm(f => ({ ...f, botType: e.target.value as AgentBotType }))}
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              {Object.entries(AGENT_BOT_TYPE_LABELS)
                .filter(([value]) => value !== AgentBotType.Webhook)
                .map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
            </select>
          </div>
          {requiresCompatibleWorkspace && (
            <fieldset className="space-y-3">
              <legend className="block text-sm font-medium text-gray-700 mb-1">Agente</legend>
              <p className="text-sm text-gray-600">
                Escolha o agente (workspace) que responderá nas caixas de atendimento. Ele precisa ter escopo documental
                compatível com canal externo.
              </p>

              {workspaces.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhum agente disponível.</p>
              ) : (
                <div className="space-y-2" role="radiogroup" aria-label="Agente do robô">
                  <label
                    className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                      form.workspaceId === ''
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="agentWorkspace"
                      value=""
                      checked={form.workspaceId === ''}
                      onChange={() => setForm(f => ({ ...f, workspaceId: '' }))}
                      className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Nenhum agente</span>
                      <p className="text-sm text-gray-600 mt-0.5">
                        O robô não responderá com base em documentos até você vincular um agente.
                      </p>
                    </div>
                  </label>

                  {workspaces.map(workspace => {
                    const selected = form.workspaceId === workspace.id;
                    const incompatible = !workspace.usableInExternalBot;
                    const restrictionReason = getExternalBotRestrictionReason(
                      workspace.documentScopeMode,
                      workspace.usableInExternalBot,
                    );
                    return (
                      <label
                        key={workspace.id}
                        aria-disabled={incompatible || undefined}
                        className={`flex items-start gap-3 rounded-lg border p-4 transition-colors ${
                          incompatible
                            ? 'cursor-not-allowed border-amber-200 bg-amber-50/70 opacity-90'
                            : selected
                              ? 'cursor-pointer border-blue-500 bg-blue-50'
                              : 'cursor-pointer border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="agentWorkspace"
                          value={workspace.id}
                          checked={selected}
                          disabled={incompatible}
                          onChange={() => {
                            if (incompatible)
                            {
                              return;
                            }

                            setForm(f => ({ ...f, workspaceId: workspace.id }));
                          }}
                          className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 disabled:cursor-not-allowed"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`font-medium ${incompatible ? 'text-gray-700' : 'text-gray-900'}`}>
                              {workspace.name}
                            </span>
                            <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                              {SCOPE_MODE_LABELS[workspace.documentScopeMode]}
                            </span>
                            {incompatible && (
                              <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">
                                Bloqueado no atendimento externo
                              </span>
                            )}
                          </div>
                          {restrictionReason && (
                            <p className="text-sm mt-0.5 text-amber-900">
                              {restrictionReason}
                            </p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {selectedWorkspaceInvalid && selectedWorkspace && (
                <div
                  role="alert"
                  className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
                >
                  <strong className="font-medium">Atenção:</strong>{' '}
                  {getExternalBotRestrictionReason(
                    selectedWorkspace.documentScopeMode,
                    selectedWorkspace.usableInExternalBot,
                  )}
                  {isAdmin ? (
                    <>
                      {' '}
                      <Link
                        to={`/chatbot-admin/workspaces/edit?id=${selectedWorkspace.id}`}
                        className="font-medium text-amber-950 underline underline-offset-2 hover:text-amber-800"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Abrir configuração do agente
                      </Link>
                    </>
                  ) : (
                    <> Peça a um administrador para ajustar o escopo documental deste agente.</>
                  )}
                </div>
              )}
            </fieldset>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => navigate(helpdeskRoutes.settingsBots)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={!canSave}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </form>
      )}

      {isEdit && activeTab === 'atuacao' && bot && (
        <AgentBotActuationSection bot={bot} inboxes={inboxes} groups={groups} />
      )}

      {isEdit && activeTab === 'classificacao' && id && (
        <AgentBotClassificationEditor botId={id} />
      )}

      {isEdit && activeTab === 'assinatura' && id && (
        <AgentBotSignatureSection botId={id} />
      )}
    </div>
  );
};
